import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { apiService } from '../services/apiService';

const AdminLoginHistoryPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState<'SUCCESS' | 'FAILED' | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const query = useMemo(() => ({
    userId: userId ? Number(userId) : undefined,
    status: status || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    page: 0,
    size: 50,
    sortBy: 'createdAt',
    sortDir: 'desc',
  }), [userId, status, fromDate, toDate]);

  const load = async (overrideQuery?: any) => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.getAdminLoginHistory(overrideQuery || query);
      setRows(res?.content || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to fetch login history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportPdf = async () => {
    try {
      const res = await apiService.getAdminLoginHistory({
        ...query,
        page: 0,
        size: 10000,
        sortBy: 'createdAt',
        sortDir: 'desc',
      });
      const data = res?.content || [];
      const rowsHtml = data.map((r: any) => `
        <tr>
          <td>${r.createdAt ?? '-'}</td>
          <td>${r.userId ?? '-'}</td>
          <td>${r.username ?? '-'}</td>
          <td>${r.status ?? '-'}</td>
          <td>${r.failureReason ?? '-'}</td>
          <td>${r.ipAddress ?? '-'}</td>
          <td>${r.location ?? '-'}</td>
          <td>${(r.osName || '-') + ' / ' + (r.browserName || '-')}</td>
          <td>${r.deviceInfo ?? '-'}</td>
        </tr>
      `).join('');
      const html = `
        <html>
          <head>
            <title>Login History Export</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 16px; }
              h2 { margin: 0 0 12px 0; }
              table { width: 100%; border-collapse: collapse; font-size: 11px; }
              th, td { border: 1px solid #ccc; padding: 6px; text-align: left; vertical-align: top; }
              th { background: #f3f3f3; }
            </style>
          </head>
          <body>
            <h2>Login History</h2>
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User ID</th>
                  <th>Username</th>
                  <th>Status</th>
                  <th>Failure Reason</th>
                  <th>IP Address</th>
                  <th>Location</th>
                  <th>OS / Browser</th>
                  <th>Device Info</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </body>
        </html>
      `;
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch {
      setError('Failed to export PDF');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Login History</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
        <Select
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          displayEmpty
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="SUCCESS">Success</MenuItem>
          <MenuItem value="FAILED">Failed</MenuItem>
        </Select>
        <TextField size="small" type="date" label="From Date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <TextField size="small" type="date" label="To Date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        <Button variant="contained" onClick={load}>Apply Filters</Button>
        <Button
          variant="text"
          onClick={() => {
            setUserId('');
            setStatus('');
            setFromDate('');
            setToDate('');
            // Load again with cleared filters (avoid relying on state timing)
            load({ page: 0, size: 50, sortBy: 'createdAt', sortDir: 'desc' });
          }}
        >
          Clear
        </Button>
        <Button variant="outlined" onClick={() => apiService.exportAdminLoginCsv(query)}>Export CSV</Button>
        <Button variant="outlined" onClick={exportPdf}>Export PDF</Button>
      </Box>
      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Failure Reason</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>OS / Browser</TableCell>
              <TableCell>Device Info</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.username} ({row.userId || '-'})</TableCell>
                <TableCell>
                  <Chip size="small" color={row.status === 'SUCCESS' ? 'success' : 'error'} label={row.status} />
                </TableCell>
                <TableCell>{row.status === 'FAILED' ? (row.failureReason || '-') : '-'}</TableCell>
                <TableCell>{row.ipAddress || '-'}</TableCell>
                <TableCell>{row.location || '-'}</TableCell>
                <TableCell>{row.osName || '-'} / {row.browserName || '-'}</TableCell>
                <TableCell title={row.deviceInfo || '-'}>
                  <Box sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.deviceInfo || '-'}
                  </Box>
                </TableCell>
                <TableCell>{row.createdAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default AdminLoginHistoryPage;
